export type CampaignGoal =
  | 'AWARENESS'
  | 'REACH'
  | 'LEAD_GENERATION'
  | 'ANNOUNCEMENT'
  | 'OTHER';

export type CampaignBuilderMode = 'ai' | 'standard';

export type MediaOrientation = 'PORTRAIT' | 'LANDSCAPE' | 'SQUARE' | string;

export type CampaignMediaItem = {
  mediaId: string;
  orientation?: MediaOrientation;
  mediaUrl?: string;
  mediaType?: string;
  /** Present when creative was picked from gallery (UI only; API gets galleryId XOR mediaIds). */
  galleryId?: string;
};

export type QrInfo = {
  title: string;
  whatsAppNumber: string;
  couponCode: string;
  productLink: string;
  predefinedMessage: string;
};

export type ManualCampaignForm = {
  agencyClientId: string;
  campaignGoals: CampaignGoal | '';
  targetCities: string[];
  customDuration: string;
  campaignName: string;
  startDate: string;
  endDate: string;
  adType: string;
  isQrIncluded: boolean;
  locationType: string;
  screenType: string;
  VenueType: string;
  VenueSubType: string;
  economicActivity: string;
  lifestyle: string;
  buyingPowerZone: string;
  industry: string;
  subIndustry: string;
  productName: string;
  company: string;
  brand: string;
  mediaIds?: CampaignMediaItem[];
  galleryId?: string;
  mediaType?: string;
  mediaDuration?: string;
  mediaUrl?: string;
  qrInfo: QrInfo;
};

export type AiCampaignForm = {
  agencyClientId: string;
  budget: string;
  startDate: string;
  endDate: string;
  targetCities: string[];
  audienceCategory: string;
  adDuration: string;
  vision: string;
  economicActivity: string;
  lifestyle: string;
  industry: string;
  subIndustry: string;
  productName: string;
  company: string;
  brand: string;
  locationType: string;
  screenType: string;
  VenueType: string;
  VenueSubType: string;
};

export type SelectedScreenMeta = {
  id: string;
  screenName: string;
  thumbnail: string;
  addressLine: string;
  city: string;
  /** Billboard screenOrientation when known (LANDSCAPE / PORTRAIT / SQUARE / CUSTOM). */
  screenOrientation?: string;
};

export type BookingSlot = {
  billboardId?: string;
  hour: number;
  date: string;
  repetitions: number;
};

export type StepTwoData = {
  billboardIds: string[];
  campaignId: string;
};

export type StepThreeData = {
  bookingSlots: BookingSlot[];
  campaignId: string;
  couponCode?: string;
  appliedToAll?: boolean;
  syncRepsAllDates?: boolean;
  fullyBookedDates?: string[];
  slotsData?: SlotsByDate;
};

export type CreativePreview = {
  url: string;
  type: string;
};

export type AvailableSlotHour = {
  hour: number;
  timeRange?: string;
  availableRepetitions?: number;
  totalRepetitions?: number;
};

export type SlotsByDate = Record<
  string,
  { availableSlots?: AvailableSlotHour[] }
>;

export type AppliedCoupon = {
  couponId?: string;
  couponCode?: string;
  couponName?: string;
  discountAmount?: number;
  discountType?: string;
  discountValue?: number;
  autoApplied?: boolean;
  benefitKey?: string;
  visibility?: string;
};

export type CampaignPricing = {
  amountWithTax?: number;
  amountWithoutTax?: number;
  /** Tax total from booking API (authoritative when taxDistribution rows are empty). */
  totalTax?: number;
  /** Final payable after discount — falls back to `amountWithTax` when absent. */
  totalAmount?: number;
  discountAmount?: number;
  /** Backend step-3 field name for coupon discount on `pricing`. */
  totalDiscountAmount?: number;
  taxBreakdown?: Record<string, number>;
};

export type TaxDistributionItem = {
  role?: string;
  percentage?: number;
  /** Step-3 summary grouped rows use `totalAmount`. */
  totalAmount?: number;
  /** Persisted campaign.taxDistribution rows use `amount`. */
  amount?: number;
};

export type PreviewSummary = {
  amountWithTax?: number;
  amountWithoutTax?: number;
  discountAmount?: number;
  totalTax?: number;
  taxBreakdown?: Record<string, number | undefined>;
  taxDistribution?: TaxDistributionItem[];
};

export type PreviewData = {
  campaignId?: string;
  campaignName?: string;
  campaignGoals?: string;
  startDate?: string;
  endDate?: string;
  customDuration?: number | string;
  requiredOrientation?: MediaOrientation[] | MediaOrientation | string;
  pricing?: CampaignPricing;
  summary?: PreviewSummary;
  coupon?: AppliedCoupon;
  analytics?: Record<string, unknown>;
  billboardWise?: unknown[];
  bookedSlots?: unknown[];
  mediaDetails?: unknown;
  targetCities?: string[];
  distributionInfo?: Record<string, unknown>;
};

export type AiPreviewScreen = {
  /** Client-only: this screen was added by the user, not picked by the AI. */
  addedByUser?: boolean;
  _id?: string;
  screenName?: string;
  screenType?: string;
  VenueType?: string;
  address?: {
    area?: string;
    city?: string;
    state?: string;
    building?: string;
    fullAddress?: string;
  };
  analytics?: {
    totalViews?: number;
    avgViews?: number;
    femaleViews?: number;
    maleViews?: number;
  };
  basePrice?: number;
  pricing?: {
    totalRepetitions?: number;
    totalDuration?: number;
    totalSlotPrice?: number;
    amountWithoutTax?: number;
    amountWithTax?: number;
    totalTax?: number;
  };
  thumbnail?: { url?: string };
  /** Optional AI rationale for selecting this screen */
  selectionReason?: string;
  aiReason?: string;
  aiDescription?: string;
  description?: string;
  reason?: string;
};

export type AiPreviewData = {
  campaignId?: string;
  /** Same screens as `screens`, but with pricing/analytics the AI plan computed. */
  screensDetail?: AiPreviewScreen[];
  suggestedName?: string;
  aiResponseDescription?: string;
  estimatedReach?: number;
  requiredOrientation?: MediaOrientation[];
  /** Client-only: the orientation the AI first recommended, kept across re-plans. */
  aiRecommendedOrientation?: MediaOrientation;
  /** Client-only: screens or plays were added locally and still need saving on the server. */
  pendingSave?: boolean;
  screens?: AiPreviewScreen[];
  timeSlots?: Array<{
    date?: string;
    hour?: number;
    /** Count or list of billboard ids / objects */
    billboards?:
      | number
      | string[]
      | Array<{ _id?: string; id?: string; screenName?: string; billboardId?: string; repetitions?: number; cost?: number }>;
    totalRepetitions?: number;
    totalCost?: number;
  }>;
  slotsDetail?: Array<{
    billboardId?: string;
    date?: string;
    hour?: number;
    repetitions?: number;
    duration?: number;
    availableSlots?: number;
    perSlotPrice?: number;
  }>;
  summary?: {
    totalScreens?: number;
    totalSlots?: number;
    totalRepetitions?: number;
    campaignDays?: number;
    totalBillboards?: number;
    totalDates?: number;
    customDuration?: number;
    totalDuration?: number;
    totalSlotPrice?: number;
    totalBaseAmount?: number;
    amountWithTax?: number;
    amountWithoutTax?: number;
    discountAmount?: number;
    totalTax?: number;
    taxBreakdown?: Record<string, number | undefined>;
    taxDistribution?: TaxDistributionItem[];
  };
  estimatedCost?: {
    total?: number;
    baseAmount?: number;
    amountWithoutTax?: number;
    finalPayableAmount?: number;
    remaining?: number;
    discountAmount?: number;
    totalTax?: number;
    utilizationPercentage?: number | string;
    taxDistribution?: TaxDistributionItem[];
    taxBreakdown?: Record<string, number | undefined>;
  };
  coupon?: AppliedCoupon;
  preview?: {
    campaignName?: string;
    campaignId?: string;
    startDate?: string;
    endDate?: string;
    customDuration?: number;
    billboardsData?: Array<{ billboardId?: string; billboardName?: string }>;
  };
  payload?: Record<string, unknown>;
};

export type CampaignApiResponse<T = unknown> = {
  success?: boolean;
  message?: string;
  data?: T;
};

export const emptyManualForm = (): ManualCampaignForm => ({
  agencyClientId: '',
  campaignGoals: '',
  targetCities: [],
  customDuration: '30',
  campaignName: '',
  startDate: '',
  endDate: '',
  adType: '',
  isQrIncluded: false,
  locationType: '',
  screenType: '',
  VenueType: '',
  VenueSubType: '',
  economicActivity: '',
  lifestyle: '',
  buyingPowerZone: '',
  industry: '',
  subIndustry: '',
  productName: '',
  company: '',
  brand: '',
  qrInfo: {
    title: '',
    whatsAppNumber: '',
    couponCode: '',
    productLink: '',
    predefinedMessage: '',
  },
});

export const emptyAiForm = (): AiCampaignForm => ({
  agencyClientId: '',
  budget: '',
  startDate: '',
  endDate: '',
  targetCities: [],
  audienceCategory: '',
  adDuration: '30',
  vision: '',
  economicActivity: '',
  lifestyle: '',
  industry: '',
  subIndustry: '',
  productName: '',
  company: '',
  brand: '',
  locationType: '',
  screenType: '',
  VenueType: '',
  VenueSubType: '',
});
